import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

export default function Projects() {
  const [projects, setProjects] = useState([]);
  const [services, setServices] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [projectsRes, servicesRes] = await Promise.all([
          fetch('https://api.dev.auxo.dk/public/content/projekter'),
          fetch('https://api.dev.auxo.dk/public/content/ydelser')
        ]);

        if (projectsRes.ok) {
          const projectsData = await projectsRes.json();
          setProjects(projectsData);
        }
        if (servicesRes.ok) {
          const servicesData = await servicesRes.json();
          setServices(servicesData);
        }
      } catch (err) {
        console.error('Error fetching data:', err);
      }
    };

    fetchData();
  }, []);

  // Find unique categories that actually have projects
  const availableCategories = React.useMemo(() => {
    const categoriesInProjects = new Set(
      projects
        .map((p) => p.tilknyttet_ydelse)
        .filter(Boolean)
    );

    return services.filter((service) => 
      categoriesInProjects.has(service.slug?.da || service.slug)
    );
  }, [projects, services]);

  const filteredProjects = selectedCategory === 'all'
    ? projects
    : projects.filter((p) => p.tilknyttet_ydelse === selectedCategory);

  return (
    <>
      <Helmet>
        <title>Udførte projekter | Referencer og byggeprojekter på Djursland | Tømrerfirmaet RNS</title>
        <meta name="description" content="Se vores udførte tømrer- og byggeprojekter på Djursland. Gennemse billeder og beskrivelser af nyt tag, træterrasser, vinduesudskiftning og sommerhusrenovering." />
        <meta property="og:title" content="Udførte projekter | Referencer og byggeprojekter på Djursland | Tømrerfirmaet RNS" />
        <meta property="og:description" content="Se vores udførte tømrer- og byggeprojekter på Djursland. Gennemse billeder og beskrivelser af nyt tag, træterrasser, vinduesudskiftning og sommerhusrenovering." />
        <meta property="og:image" content="https://assets.auxodev.dk/assets/d1f69564-4a06-4909-b6f1-fa1f6ad6911e/image00125.jpeg" />
        <link rel="canonical" href="https://rns.dk/referencer" />
        <html lang="da-DK" />
      </Helmet>

      {/* Projects List Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 space-y-16">
          {/* Page Title */}
          <div className="text-center space-y-2 max-w-2xl mx-auto">
            <h1 className="font-display font-extrabold text-4xl text-black">
              Vores Projekter
            </h1>
            <p className="text-black">
              Vi sætter en stor ære i vores arbejde. Her kan du læse om og se billeder af et udvalg af vores udførte projekter.
            </p>
          </div>

          {/* Filter Bar */}
          {availableCategories.length > 0 && (
            <div className="flex flex-wrap justify-center items-center gap-x-8 gap-y-3 border-b border-black/10 pb-6">
              <button
                onClick={() => setSelectedCategory('all')}
                className={`pb-2 text-sm font-bold uppercase tracking-wider transition-all duration-200 rounded-none border-b-2 cursor-pointer ${
                  selectedCategory === 'all'
                    ? 'border-black text-black'
                    : 'border-transparent text-black/50 hover:text-black hover:border-black/30'
                }`}
              >
                Alle
              </button>
              {availableCategories.map((cat) => {
                const slug = cat.slug?.da || cat.slug;
                const title = cat.titel?.da || cat.titel;
                return (
                  <button
                    key={slug}
                    onClick={() => setSelectedCategory(slug)}
                    className={`pb-2 text-sm font-bold uppercase tracking-wider transition-all duration-200 rounded-none border-b-2 cursor-pointer ${
                      selectedCategory === slug
                        ? 'border-black text-black'
                        : 'border-transparent text-black/50 hover:text-black hover:border-black/30'
                    }`}
                  >
                    {title}
                  </button>
                );
              })}
            </div>
          )}

          <div className="space-y-20 pt-4">
            {filteredProjects.map((project, index) => (
              <div
                key={project.id}
                className={`grid grid-cols-1 lg:grid-cols-12 gap-10 items-center ${
                  index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                }`}
              >
                {/* Image Column */}
                <div className="lg:col-span-6 space-y-4">
                  {project.billeder && project.billeder[0] && (
                    <Link
                      to={`/referencer/${project.slug?.da || project.slug}`}
                      className="block w-full h-64 sm:h-80 rounded-none overflow-hidden border border-black/10 shadow-sm cursor-pointer focus:outline-none focus:ring-2 focus:ring-black"
                    >
                      <img
                        src={`https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/${project.billeder[0].replace('https://assets.auxodev.dk/', '')}`}
                        alt={`${project.titel?.da || project.titel} - Udført projekt på Djursland af Tømrerfirmaet RNS`}
                        className="w-full h-full object-cover hover:scale-102 transition-transform duration-300"
                      />
                    </Link>
                  )}

                  {/* Additional Images Row */}
                  {project.billeder && project.billeder.length > 1 && (
                    <div className="grid grid-cols-3 gap-3">
                      {project.billeder.slice(1).map((imgUrl, idx) => (
                        <Link
                          key={idx}
                          to={`/referencer/${project.slug?.da || project.slug}`}
                          className="block h-20 rounded-none overflow-hidden border border-black/10 shadow-sm cursor-pointer focus:outline-none focus:ring-2 focus:ring-black"
                        >
                          <img
                            src={`https://assets.auxodev.dk/cdn-cgi/image/width=200,height=150,fit=cover,quality=80,format=auto/${imgUrl.replace('https://assets.auxodev.dk/', '')}`}
                            alt={`${project.titel?.da || project.titel} - Detaljebillede ${idx + 1} fra Tømrerfirmaet RNS`}
                            className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                          />
                        </Link>
                      ))}
                    </div>
                  )}
                </div>

                {/* Text Column */}
                <div className="lg:col-span-6 space-y-4">
                  <h2 className="font-display font-extrabold text-2xl sm:text-3xl text-black">
                    <Link
                      to={`/referencer/${project.slug?.da || project.slug}`}
                      className="hover:underline text-black"
                    >
                      {project.titel.da}
                    </Link>
                  </h2>
                  <div
                    className="text-black leading-relaxed space-y-4 text-sm sm:text-base line-clamp-3"
                    dangerouslySetInnerHTML={{ __html: project.beskrivelse.da }}
                  />
                  <div className="pt-4 flex flex-wrap gap-x-6 gap-y-2">
                    <Link
                      to={`/referencer/${project.slug?.da || project.slug}`}
                      className="inline-flex items-center gap-2 text-black hover:underline font-bold text-sm"
                    >
                      Læs mere og se billeder
                      <i className="fa-solid fa-arrow-right text-xs" />
                    </Link>
                  </div>
                </div>
              </div>
            ))}

            {filteredProjects.length === 0 && (
              <div className="text-center py-12 text-black">
                <i className="fa-solid fa-hammer text-4xl mb-3 block text-black" />
                Ingen projekter fundet i denne kategori.
              </div>
            )}
          </div>
        </div>
      </section>
    </>
  );
}
